import React, { FunctionComponent } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { FinanceType } from '@upstart/harmony/lib/constants';
import { DeskingOfferHistoryInfo } from '@upstart/harmony/lib/legos/deskingTypes';
import {
  PropertyChange,
  PropertyType,
} from '@upstart/harmony/lib/legos/propertyChangeTypes';
import { PrimitiveProperty, isPropertyChanged } from './PrimitiveProperty';
import { ListProperty, isListChangeEmpty } from './ListProperty';
import './VersionInfo.scss';
import { UNSAVED_VERSION } from '../Constants';

interface VersionInfoProps {
  offerHistoryInfo: DeskingOfferHistoryInfo;
  offerHistory: DeskingOfferHistoryInfo[];
  currentVersion: boolean;
  isSelected: boolean;
  setSelectedVersion: (version: number) => void;
}

export const VersionInfo: FunctionComponent<VersionInfoProps> = ({
  offerHistoryInfo,
  offerHistory,
  currentVersion,
  isSelected,
  setSelectedVersion,
}) => {
  const date = new Date(offerHistoryInfo.createdAt);
  let restoredFromDate: Date | null = null;
  if (offerHistoryInfo.parentId) {
    const restoredFromOffer = offerHistory.find(
      offer => offer.id === offerHistoryInfo.parentId,
    );
    if (restoredFromOffer) {
      restoredFromDate = new Date(restoredFromOffer.createdAt);
    }
  }

  const renderChanges = (change: PropertyChange): JSX.Element => {
    if (change.type === 'primitive') {
      return (
        <PrimitiveProperty
          change={change}
          financeType={
            offerHistoryInfo.data?.financeType ?? FinanceType.PURCHASE
          }
        />
      );
    }
    if (change.type === 'list') {
      return (
        <ListProperty
          change={change}
          financeType={
            offerHistoryInfo.data?.financeType ?? FinanceType.PURCHASE
          }
        />
      );
    }
    return <div />;
  };

  return (
    <div
      data-testid="version-info"
      className={classNames('version-info-container', {
        selected: isSelected,
      })}
      onClick={() => setSelectedVersion(offerHistoryInfo.version)}
    >
      <div className="version-info-date">
        {moment(date).format('MMMM DD, YYYY h:mm A')}
      </div>
      {offerHistoryInfo.version === UNSAVED_VERSION && (
        <div className="version-info-subtitle">Pending changes</div>
      )}
      {currentVersion && (
        <div className="version-info-subtitle">Latest saved version</div>
      )}
      {offerHistoryInfo.isDefaultOffer && (
        <div className="version-info-subtitle">Default pencil</div>
      )}
      {offerHistoryInfo.clonedFrom && (
        <div className="version-info-subtitle">
          Duplicated from other pencil
        </div>
      )}
      {restoredFromDate && (
        <div className="version-info-subtitle">
          Restored from{' '}
          {moment(restoredFromDate).format('MMMM DD, YYYY h:mm A')}
        </div>
      )}
      {offerHistoryInfo.propertyChanges && !restoredFromDate && (
        <div
          data-testid="version-info-change"
          className="version-info-change-container"
        >
          {offerHistoryInfo.propertyChanges
            .filter(
              change =>
                (change.type === PropertyType.PRIMITIVE &&
                  isPropertyChanged(change)) ||
                (change.type === PropertyType.LIST &&
                  !isListChangeEmpty(change)),
            )
            .map(change => renderChanges(change))}
        </div>
      )}
      {!offerHistoryInfo.isDefaultOffer &&
        offerHistoryInfo.version !== UNSAVED_VERSION && (
          <div
            className={classNames('version-info-username', {
              'with-spacer':
                !offerHistoryInfo.propertyChanges || restoredFromDate,
            })}
          >{`by ${offerHistoryInfo.createdByName}`}</div>
        )}
    </div>
  );
};

export const isVersionPropChangeEmpty = (
  historyInfo: DeskingOfferHistoryInfo,
) => {
  if (
    !historyInfo.propertyChanges ||
    historyInfo.propertyChanges.length === 0
  ) {
    return true;
  }
  if (
    historyInfo.propertyChanges.every(
      change =>
        (change.type === PropertyType.PRIMITIVE &&
          !isPropertyChanged(change)) ||
        (change.type === PropertyType.LIST && isListChangeEmpty(change)),
    )
  ) {
    return true;
  }
  return false;
};
